import React, { useEffect, useState } from "react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import OTPModal from "./OTPModal";
import PrimaryButton from "../../components/ui/PrimaryButton";
import { useNavigate } from "react-router-dom";
import apiClient from "../../utils/apiClient";
import { createWaitlistEntry, sendOtp } from "../../services/userService";
import { countryList } from "../../utils/utils";
import { Header } from "../../components/Header";
import {
  DARK_PURPLE_THEME_COLOR,
  LIGHT_PURPLE_THEME_COLOR,
} from "../../utils/css_colors";

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .min(1, "Name must be atleast 1 character")
    .required("Name is required"),
  countryCode: Yup.string().required("Country Code is required"),
  phone: Yup.string()
    .matches(/^[0-9]{10}$/, "Phone number is not valid")
    .required("Phone number is required"),
  company_name: Yup.string()
    .min(1, "Company cannot be empty")
    .required("Company name is required"),
  email: Yup.string().email("Email is not valid").required("Email is required"),
  message: Yup.string(),
});

const MyForm = () => {
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <div className="w-screen flex flex-col pt-12 pb-40">
        {isEmailVerified ? (
          <div className="flex flex-col h-[calc(100vh-9rem)] gap-8 justify-center items-center gap-4 px-16 text-center">
            <div className="text-xl">
              Thank you for your interest. We will reach out to you soon!
            </div>
            <button
              onClick={() => {
                navigate("/");
              }}
              type="submit"
              className={`border-[1px] rounded-md text-white px-5 py-[6px] bg-[${DARK_PURPLE_THEME_COLOR}]`}>
              Back
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <h1 className="text-2xl font-extrabold tracking-tight leading-none text-gray-900 md:text-2xl lg:text-2xl dark:text-white pb-4">
              Get in touch with us
            </h1>
            <div className="mx-4 sm:w-[25rem] border border-slate-400 shadow-lg p-4 rounded-xl shadow">
              <Formik
                initialValues={{
                  name: "",
                  countryCode: "+91",
                  phone: "",
                  company_name: "",
                  email: "",
                  message: "",
                }}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                  const formData = new FormData();
                  formData.append("email_id", values.email);
                  formData.append(
                    "phone_number",
                    values.countryCode + values.phone
                  );
                  formData.append("name", values.name);
                  formData.append("company_name", values.company_name);
                  formData.append("message", values.message);

                  createWaitlistEntry(formData);
                  // Use a new api here to create a waitlist entry and send a slack notification.

                  setIsEmailVerified(true);
                  // setOtpLoading(true);
                  // sendOtp(data)
                  //   .then((response) => {
                  //     setModalIsOpen(true);
                  //   })
                  //   .catch((error) => console.error("Error:", error));
                }}>
                {({ errors, touched, values }) => (
                  <Form className="space-y-4">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700">
                        <span className="text-red-500">*</span>Name
                      </label>
                      <Field
                        name="name"
                        type="text"
                        className={
                          "mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" +
                          (errors.name && touched.name ? " border-red-500" : "")
                        }
                      />
                      <ErrorMessage
                        name="name"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>
                    <div className="flex w-full gap-3">
                      <div className="w-[50%]">
                        <label
                          htmlFor="countryCode"
                          className="block text-sm font-medium text-gray-700">
                          <span className="text-red-500">*</span>Phone
                        </label>
                        <Field
                          as="select"
                          name="countryCode"
                          className={
                            "mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" +
                            (errors.countryCode && touched.countryCode
                              ? " border-red-500"
                              : "")
                          }>
                          {countryList.map((country, index) => (
                            <option
                              key={country.code}
                              value={country.dial_code}>
                              {`${country.flag} (${country.dial_code})      ${country.code} ${country.name}`}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage
                          name="countryCode"
                          component="div"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>

                      <div className="w-full relative top-3 flex flex-col gap-2">
                        <label
                          htmlFor="phone"
                          className="block text-sm font-medium text-gray-700">
                          {" "}
                        </label>
                        <div className="gap-3">
                          <Field
                            name="phone"
                            type="number"
                            className={
                              "mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" +
                              (errors.phone && touched.phone
                                ? " border-red-500"
                                : "")
                            }
                          />
                          <ErrorMessage
                            name="phone"
                            component="div"
                            className="text-red-500 text-sm mt-1"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="company_name"
                        className="block text-sm font-medium text-gray-700">
                        <span className="text-red-500">*</span>Company Name
                      </label>
                      <Field
                        name="company_name"
                        type="text"
                        className={
                          "mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" +
                          (errors.company_name && touched.company_name
                            ? " border-red-500"
                            : "")
                        }
                      />
                      <ErrorMessage
                        name="company_name"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700">
                        <span className="text-red-500">*</span>Official Email
                      </label>
                      <Field
                        name="email"
                        type="email"
                        className={
                          "mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" +
                          (errors.email && touched.email
                            ? " border-red-500"
                            : "")
                        }
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="message"
                        className="block text-sm font-medium text-gray-700">
                        <span className="text-red-500"></span>Message (optional)
                      </label>
                      <Field
                        as="textarea"
                        name="message"
                        className={
                          "mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md h-[5rem]" +
                          (errors.message && touched.message
                            ? " border-red-500"
                            : "")
                        }
                      />
                      <ErrorMessage
                        name="message"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>
                    <div className="w-full flex flex-col gap-2 justify-center z-30">
                      <button
                        type="submit"
                        className={`border-[1px] rounded-lg text-white my-2 px-5 py-[6px] ${
                          otpLoading
                            ? "bg-gray-300 text-slate-600"
                            : `bg-[${DARK_PURPLE_THEME_COLOR}] border-[${DARK_PURPLE_THEME_COLOR}]`
                        }`}
                        disabled={otpLoading}>
                        Request Callback
                      </button>
                    </div>

                    <div>
                      <OTPModal
                        modalIsOpen={modalIsOpen}
                        setModalIsOpen={setModalIsOpen}
                        setIsEmailVerified={setIsEmailVerified}
                        parentValues={values}
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MyForm;
